import type {
  ElectronicForm,
  ElectronicFormNew,
  AqmsFile,
  ElectronicFormBE,
  ElectronicFormSubmissionData,
  ElectronicFormValidationData,
  TraverseAndMutateMethod,
  PageToPdfConfig,
  PageToPdfRequest,
  StandardFormRouteNames,
} from "@/views/electronicForm/electronicForm";
import { useApi, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { LE } from "@/constants/enums";
import { getDocumentBaseUrl, type DocumentNew, type FileUploadResponse } from "@modernary/mui/components/document";
import { saveFileUpload } from "@/services/endpoints/fileUploadApi";
import type { MuiFile, MuiFileUpload } from "@modernary/mui/utils/fk";
import { ref } from "vue";
import type { AuditUser } from "@modernary/mui/components/audit";
import type { AxiosResponse } from "axios";
import { routeNames } from "@/routes";
import { type RouteLocationNormalizedLoadedGeneric, type Router, type RouteRecordName } from "vue-router";
import { editRouteCancelAction } from "@modernary/mui/utils/helpers";
import { AuthRequest, getStatusMurnChangeResource, getUpdateResource } from "@modernary/mui/utils/authorization";
import type { ActionsButtonItemsProp } from "@modernary/mui/components/button";
import { useElectronicFormStore } from "@/views/electronicForm/electronicFormStore";
import { clone } from "@formkit/utils";

export default {
  uploadDocuments,
  addAuthRequestFields,
  getInitialData,
  responseAdapter,
  requestAdapter,
  useDeleteFileUploadIds,
  getHandlers,
  getCancelHandler,
  useCommonAuthRequests,
  cancelAction,
  getRouteFullUrl,
  getUrlsFromRouteLocations,
  // ElectronicForm stores the form as JSON, so it has no need for FK conversions.
  // fromFormkit,
  // toFormkit,
};

function getRouteFullUrl(name: RouteRecordName, router: Router) {
  const route = router.resolve({ name });
  return location.origin + route.href;
}

function getUrlsFromRouteLocations(config: PageToPdfConfig, router: Router): PageToPdfRequest[] {
  const result = config.locations.map((location) => {
    const routeUrl = getRouteFullUrl(location.routeLocation as RouteRecordName, router);
    return { url: routeUrl, fileName: location.fileName };
  });

  return result;
}

function useDeleteFileUploadIds() {
  const deleteFileUploadIds = ref<string[]>([]); // An array of fileUploadIds used to delete files from the database
  const handleDeleteFile = (event: { payload: AqmsFile[] }) => {
    event.payload.forEach((file) => {
      if (file.fileUploadId) {
        deleteFileUploadIds.value.push(file.fileUploadId);
      }
    });
  };
  const addDeleteFileHandler = (node: any) => {
    node.on("deleteFile", handleDeleteFile);
  };
  return { deleteFileUploadIds, handleDeleteFile, addDeleteFileHandler };
}

function getCancelHandler(
  showConfirmation: (title: string, description?: string) => Promise<boolean>,
  electronicFormStore: ReturnType<typeof useElectronicFormStore>,
  saveElectronicForm: (
    electronicForm: ElectronicFormBE,
    config?: {},
  ) => Promise<AxiosResponse<ElectronicFormBE, any, {}>>,
) {
  const { exec: execSaveElectronicForm } = useApi(saveElectronicForm, {
    responseAdapter: async (response) => {
      return await responseAdapter(response);
    },
  });

  const cancelSubmissionHandler = async () => {
    if (!electronicFormStore.electronicForm) return; // Should never happen.

    const cancelled = await showConfirmation(
      "Cancel Submission",
      "Cancel the submission? This will return the form to Draft and enable editing.",
    );
    if (!cancelled) return;

    const eForm = await requestAdapter(clone(electronicFormStore.electronicForm));
    eForm.statusMurn = LE.ElectronicFormStatus.Draft;
    const result = await execSaveElectronicForm(eForm);
    if (!result) return; //TODO:?

    electronicFormStore.fill(result);
  };

  return cancelSubmissionHandler;
}

function getHandlers(
  formRouteNames: StandardFormRouteNames,
  router: Router,
  route: RouteLocationNormalizedLoadedGeneric,
) {
  return {
    updateHandler: async () =>
      router.push({
        name: formRouteNames.edit,
        params: { id: route.params.id },
      }),
    reviseHandler: async () =>
      router.push({
        name: formRouteNames.new,
        params: { id: route.params.id, typeMurn: LE.ElectronicFormType.Revision },
      }),
    renewHandler: async () =>
      router.push({
        name: formRouteNames.new,
        params: { id: route.params.id, typeMurn: LE.ElectronicFormType.Renewal },
      }),
    initiateSubmissionHandler: async () =>
      router.push({ name: formRouteNames.initiateSubmission, params: { id: route.params.id } }),
    reviewSubmissionHandler: async () =>
      router.push({ name: formRouteNames.reviewSubmission, params: { id: route.params.id } }),
  };
}

/**
 * Returns a set of common auth requests and functions to add actions to a list of actions - Includes Update, Delete, and Submit.
 * @returns An object with the common auth requests and functions to add actions to a list of actions.
 */
function useCommonAuthRequests() {
  const updateElectronicFormAR = new AuthRequest();
  updateElectronicFormAR.permissionName = "UpdateElectronicForm";
  const deleteElectronicFormAR = new AuthRequest();
  deleteElectronicFormAR.permissionName = "DeleteElectronicForm";
  const submitDraftElectronicFormAR = new AuthRequest();
  submitDraftElectronicFormAR.permissionName = "UI_SubmitElectronicForm";
  const submitElectronicFormAR = new AuthRequest();
  submitElectronicFormAR.permissionName = "UI_ESignElectronicForm";
  const cancelElectronicFormAR = new AuthRequest();
  cancelElectronicFormAR.permissionName = "CancelElectronicForm";

  const setAuthResource = (resource: { statusMurn?: LE.ElectronicFormStatus }) => {
    updateElectronicFormAR.resource = getUpdateResource(resource);
    deleteElectronicFormAR.resource = resource;
    submitDraftElectronicFormAR.resource = getStatusMurnChangeResource(
      resource,
      LE.ElectronicFormStatus.SubmissionPending,
    );
    submitElectronicFormAR.resource = getStatusMurnChangeResource(resource, LE.ElectronicFormStatus.Submitted);
    cancelElectronicFormAR.resource = getStatusMurnChangeResource(resource, LE.ElectronicFormStatus.Draft);
  };

  const addActions = (
    actions: ActionsButtonItemsProp = [],
    {
      canUpdate,
      updateHandler,
      canDelete,
      deleteHandler,
      canInitiateSubmission,
      initiateSubmissionHandler,
      canReviewSubmission,
      reviewSubmissionHandler,
      canCancelSubmission,
      cancelSubmissionHandler,
    }: {
      canUpdate?: boolean;
      updateHandler: () => Promise<any>;
      canDelete?: boolean;
      deleteHandler: () => Promise<any>;
      canInitiateSubmission?: boolean;
      initiateSubmissionHandler: () => Promise<any>;
      canReviewSubmission?: boolean;
      reviewSubmissionHandler: () => Promise<any>;
      canCancelSubmission?: boolean;
      cancelSubmissionHandler: () => Promise<any>;
    },
  ) => {
    if (canUpdate) {
      actions.push({ label: "Edit", handler: updateHandler });
    }
    if (canInitiateSubmission) {
      actions.push({ label: "Submit", handler: initiateSubmissionHandler, groupId: 2 });
    }
    if (canReviewSubmission) {
      actions.push({ label: "Submit", handler: reviewSubmissionHandler, groupId: 2 });
    }
    if (canCancelSubmission) {
      actions.push({ label: "Cancel Submission", handler: cancelSubmissionHandler, variant: "danger", groupId: 2 });
    }
    if (canDelete) {
      actions.push({ label: "Delete", handler: deleteHandler, variant: "danger", groupId: 99 });
    }
  };

  return {
    updateElectronicFormAR,
    deleteElectronicFormAR,
    submitDraftElectronicFormAR,
    submitElectronicFormAR,
    cancelElectronicFormAR,
    addActions,
    setAuthResource,
  };
}

/**
 * Uploads files. By default, it's expected that the `muiFile` input type is in use; this must be used to get the additional properties from the FormKit node. (categoryMurn, typeMurn, displayName)
 * @param files The files to upload.
 * @param config.categoryMurn Overrides the category of the file.
 * @param config.typeMurn Overrides the type of the file.
 * @param config.displayName Overrides the display name of the file.
 * @returns The document requests for the uploaded files. These can then be added to a documentRequests array.
 */
async function uploadDocuments(
  files: Array<MuiFile<LE.DocumentCategory, LE.DocumentType>>,
  config?: {
    categoryMurn?: LE.DocumentCategory;
    typeMurn?: LE.DocumentType;
    displayName?: string;
  },
  // existingDocuments: Document[],
) {
  // saveFileUpload
  const { exec: execSaveFileUpload } = useApi<MuiFileUpload<LE.DocumentCategory, LE.DocumentType>, FileUploadResponse>(
    saveFileUpload,
  );

  const newFiles = files.filter((file) => file.file);
  if (newFiles.length === 0) return [];

  const uploadedDocs = await execSaveFileUpload({ files: newFiles });
  if (!uploadedDocs) {
    throw new Error("File was not uploaded.");
  }

  const result = uploadedDocs.map((doc, index) => {
    if (doc.hasError) {
      throw new Error("File was not uploaded.");
    }

    let documentToSave: DocumentNew = {};

    const fileInputData = newFiles[index];
    fileInputData.fileUploadId = doc.fileUploadId;
    delete fileInputData.file; // Remove the file data so it's not stored in the form JSON.

    //add in fields the user does not set
    documentToSave.fileUploadId = doc.fileUploadId;
    documentToSave.fileSize = doc.size;
    documentToSave.fileName = doc.name;
    documentToSave.contentType = doc.type;

    documentToSave.displayName = config?.displayName ?? fileInputData.displayName;
    documentToSave.categoryMurn = config?.categoryMurn ?? fileInputData.categoryMurn;
    documentToSave.typeMurn = config?.typeMurn ?? fileInputData.typeMurn;

    // saveObject.documentRequest = documentToSave;
    return documentToSave;
  });

  return result;
}

function addAuthRequestFields<T extends ElectronicForm | ElectronicFormNew>(entity: T) {
  entity._authRequestFields = ["submitterSubjectId", "statusMurn"];
  return entity;
}

function getInitialData() {
  const result = {} as ElectronicFormNew;
  return addAuthRequestFields(result);
}

/**
 * Traverses an object and applies a list of methods to each key-value pair. It will crawl arrays and objects.
 * The methods are applied in parallel, but the traversal is done in series, depth-first.
 * @param obj The object to traverse.
 * @param methods An array of methods to apply to each key-value pair.
 * @param rootObject Initial calls should NOT pass this or should pass the same object as obj.
 */
async function traverseAndMutateObject<T extends Record<string, any> = Record<string, any>>(
  obj: T,
  methods: Array<TraverseAndMutateMethod<T>>,
  rootObject: T = obj,
) {
  if (!obj || typeof obj !== "object") return;

  for (const [key, val] of Object.entries(obj)) {
    // Execute each method on the current key-value pair
    await Promise.all(methods.map((method) => method(key, val, { currentObject: obj, rootObject })));

    // Recursively traverse nested objects and arrays
    if (val && typeof val === "object") {
      if (Array.isArray(val)) {
        // Handle arrays by traversing each element
        await Promise.all(
          val.map(async (item) => {
            if (item && typeof item === "object") {
              await traverseAndMutateObject(item, methods, rootObject);
            }
          }),
        );
      } else {
        // Handle regular objects
        await traverseAndMutateObject(val, methods, rootObject);
      }
    }
  }
}

async function responseAdapter(response: AxiosResponse<ElectronicFormBE>) {
  const { submissionData, validationData, updatedBy, ...data } = response.data;
  let parsedAuditUser: AuditUser | undefined;
  let parsedSubmissionData: ElectronicFormSubmissionData;
  let parsedValidationData: ElectronicFormValidationData;
  try {
    if (typeof submissionData === "string") {
      parsedSubmissionData = submissionData ? JSON.parse(submissionData) : { primaryForm: undefined };
    } else {
      parsedSubmissionData = submissionData;
    }
  } catch (error) {
    // If the JSON is invalid for some reason, we'll return an empty object.
    parsedSubmissionData = { primaryForm: undefined };
    console.error("Invalid JSON for submissionData", error);
  }
  try {
    if (typeof validationData === "string") {
      parsedValidationData = validationData ? JSON.parse(validationData) : { isFormValid: false };
    } else {
      parsedValidationData = validationData;
    }
  } catch (error) {
    parsedValidationData = { isFormValid: false };
    console.error("Invalid JSON for validationData", error);
  }

  try {
    if (typeof updatedBy === "string") {
      parsedAuditUser = updatedBy ? JSON.parse(updatedBy) : {};
    } else {
      parsedAuditUser = updatedBy;
    }
  } catch (error) {
    parsedAuditUser = { id: "", email: "", name: "" };
    console.error("Invalid JSON for auditUser", error);
  }

  await traverseAndMutateObject(parsedSubmissionData, [
    async (key, val, context) => {
      if (key.endsWith("_file") && Array.isArray(val)) {
        //  context.currentObject["_" + key + "_download"] = getDownloadObjectForFileUpload(val);
        context.currentObject["_" + key + "_document"] = val.map((x) =>
          response.data.documents?.find((doc) => doc.fileUploadId === x.fileUploadId),
        );
      }
    },
  ]);

  //TS doesn't handle spread operators well, so we need the "as" to tell it what it is.
  const result = {
    submissionData: parsedSubmissionData,
    validationData: parsedValidationData,
    updatedBy: parsedAuditUser,
    ...data,
  } as ElectronicForm;

  addAuthRequestFields(result);
  return result;
}

const removeClientSideOnlyFields: TraverseAndMutateMethod<any> = async (key, val, context) => {
  if (key.startsWith("_")) {
    delete context.currentObject[key];
  }
};

async function requestAdapter({
  submissionData,
  validationData,
  updatedBy: auditUser,
  electronicFormRequests,
  group,
  type,
  form,
  status,
  ...data
}: ElectronicForm) {
  await traverseAndMutateObject(submissionData, [removeClientSideOnlyFields]);
  await traverseAndMutateObject(validationData, [removeClientSideOnlyFields]);

  let submissionDataString;
  if (typeof submissionData !== "string") {
    submissionDataString = JSON.stringify(submissionData);
  }
  let validationDataString;
  if (typeof validationData !== "string") {
    validationDataString = JSON.stringify(validationData);
  }
  const result = {
    submissionData: submissionDataString,
    validationData: validationDataString,
    ...data,
    // intentionally do not include electronicFormRequests, group, type, form, status
  } as ElectronicFormBE;

  return result;
}

function cancelAction({
  router,
  viewRouteName,
  isNew,
  typeMurn,
  viewParams,
}: {
  router: Router;
  viewRouteName: string;
  isNew: boolean;
  typeMurn?: LE.ElectronicFormType;
  viewParams: any;
}) {
  // We do this because Revision and Renewal are created from the view page, so when cancelling, they behave like an "edit".
  const isAllNew = isNew && typeMurn === LE.ElectronicFormType.New;

  editRouteCancelAction({
    router,
    viewRouteName,
    listRouteName: routeNames.electronicForm.stationarySourcePermittingSelect,
    isNew: isAllNew,
    viewParams,
  });
}
